// i18n
import { translate, translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import DashboardServiceNavigation, {
  SectionAdminView,
  SectionMemberView,
} from 'presentations/views/components/organisms/DashboardServiceNavigation';

import styled from '@emotion/styled';
import { blueGrayPale, primary, white } from 'presentations/views/styles/color';
import DashboardPageTemplate from 'presentations/views/components/templates/DashboardPageTemplate';
import TaxRateList from 'presentations/views/components/organisms/TaxRateList';
import ConfirmationDialog from 'presentations/views/components/molecules/ConfirmationDialog';
import {
  spacing,
  fontSize,
  mediaMax,
  borderRadius,
  fontWeight,
} from 'presentations/views/styles';
import Button from 'presentations/views/components/atoms/Button';
import Icon from 'presentations/views/components/atoms/Icon';
import { ViewerItem } from 'presentations/api/schema/user';
import Pagination from 'presentations/views/components/atoms/Pagination';
import Avatar from 'presentations/views/components/atoms/Avatar';
import { useTaxRateListPage } from './hooks';
import ServiceHeaderTitle from 'presentations/views/components/organisms/ServiceHeaderTitle';
import Image from 'presentations/views/components/atoms/Image';
import { useContext } from 'react';
import { DashboardPortalContext } from 'presentations/reducers/DashboardPortal';
import { Role } from 'domains/models/team_user/role';

interface Props {
  viewer?: ViewerItem;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const { state: dashboardState } = useContext(DashboardPortalContext);
  const viewer = props.viewer || dashboardState.viewer!;
  const { state, actions } = useTaxRateListPage({
    viewer,
  });

  return (
    <DashboardPageTemplate
      title={t('title')}
      viewer={viewer}
      customHeader={
        <DashboardServiceNavigation
          viewer={viewer}
          activeIndex={
            viewer.role > Role.Admin
              ? SectionMemberView.taxRate
              : SectionAdminView.taxRate
          }
        />
      }
      tabBarContent={
        <>
          <Container>
            <ActionContainer>
              <AvatarContainer>
                <ProfileName>{t('Tax Rate')}</ProfileName>
              </AvatarContainer>
              {viewer.role <= Role.Admin && (
                <ButtonWrap
                  variant="text"
                  size="small"
                  color="primary"
                  onClick={actions.onClickNewTaxRate}
                  dataProps={{
                    'data-intercom-target': 'service-create-button',
                  }}
                >
                  <ButtonIcon name="add" />
                  {t('Create')}
                </ButtonWrap>
              )}
            </ActionContainer>
            <TaxRateList
              isLoading={state.isLoading}
              viewer={viewer}
              taxRates={state.taxRates}
              onSelectMenu={actions.onSelectMenu}
            />
            {state.numberOfPages > 1 && (
              <PaginationContainer>
                <Pagination
                  currentPage={state.currentPage}
                  numberOfPages={state.numberOfPages}
                  onChange={actions.onChangePage}
                />
              </PaginationContainer>
            )}
            <ConfirmationDialog
              isLoading={state.isConfirmLoading}
              isOpen={state.deleteTaxRate !== null}
              onSubmit={actions.onDeleteTaxRate}
              onCancel={actions.onCloseDialog}
              title={t('Delete?')}
              submitLabel={t('Delete')}
            />
          </Container>
        </>
      }
    />
  );
}

const Container = styled.div`
  background: ${blueGrayPale};
  padding: ${spacing(24)} ${spacing(24)} 0;
  ${mediaMax.tablet} {
    width: 100%;
    padding: ${spacing(12)} ${spacing(12)} 0;
  }
`;

const HostEventTypeListContainer = styled.div`
  margin-top: ${spacing(48)};
  padding-left: ${spacing(24)};
  ${mediaMax.tablet} {
    margin-top: ${spacing(24)};
    padding: 0 ${spacing(12)};
  }
`;

const ProfileName = styled.div`
  font-size: ${fontSize(24)};
  font-weight: bold;
  ${mediaMax.tablet} {
    font-size: ${fontSize(18)};
  }
`;
const ActionContainer = styled.div`
  margin: 0 auto ${spacing(32)};
  display: flex;
  justify-content: space-between;
  ${mediaMax.tablet} {
    display: block;
    width: 100%;
  }
  text-align: right;
`;
const ButtonWrap = styled(Button)`
  width: 230px;

  ${mediaMax.tablet} {
    width: 100%;
  }
`;
const ButtonIcon = styled(Icon)`
  margin-right: ${spacing(8)};
`;

const AvatarContainer = styled.div`
  display: flex;
  align-items: center;
  ${mediaMax.tablet} {
    /* margin-bottom: ${spacing(24)}; */
  }
`;

const PaginationContainer = styled.div`
  padding: ${spacing(48)} 0;
  display: flex;
  justify-content: center;
`;
